// Header strip that renders ABOVE the Trade panel on single-token tabs
// only. Layout: left-side TOKEN/USDC + TODAY label + value tiles for
// ATMF 1W/1M and 25RR 1W/1M, fetched in one cm_time_series round-trip
// every 30s.
//
// Each tile's value text is tinted by where the current reading sits in
// today's [low, high] range — green near the high, neutral in the middle,
// red near the low. Box chrome stays neutral so the row reads calm.
//
// REGRESSION GUARD: any change to the wire shape, query list or polling
// logic should be verified by running:
//   immersive_webapp/scripts/test_locked_token_header_wire.py
// against DEMO. It exercises the exact cm_time_series payload this file
// sends for both BTC and ETH and exits non-zero on any failure.
import { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Stack, Typography, keyframes, useTheme } from '@mui/material';

import { SUBSCRIPTIONS_KEY } from '../../constants/websocket-events';
import { CurrencyTypeEnum } from '../../model/currency_type.enum';
import { DataLabelEnum } from '../../model/data_label.enum';
import { IntervalEnum } from '../../model/interval.enum';
import { SourceTypeEnum, normalizeExchangeToSource } from '../../model/source_type.enum';
import { CandleRequest } from '../../providers/model/candleData';
import { TokenDataRequest } from '../../providers/model/interfaces/token_data';
import { VolumeMultipleRequest } from '../../providers/model/volumeData';
// (CurrencyTypeEnum, Token imports kept for the TokenDataRequest signature.)
// Subscribe.ticker's symbol is built as `${currency}-${label}` — we cast
// the base_quote string into CurrencyTypeEnum so any registry pair works
// (e.g. "SOL_USDC" → "SOL_USDC-PERPETUAL", not just BTC/ETH).
import { BAND_LABEL_W } from '../../components/v2/widgets/exotics-shared';
import { PricingContext } from '../../providers/exotics-pricing-provider';
import { useAvailableTokens } from '../../providers/exotics-tokens/use-available-tokens';
import { useLiveVolSurface } from '../../providers/exotics-tokens/use-live-vol-surface';
import { WebSocketContext } from '../../providers/websocket-context/websocket-provider';
import { readSurfaceVolPercent } from './surface-vol-tiles';
import { formatTokenPrice } from '../../utils/decimal-map';

// Soft tint flash used when a vol tile gets a fresh poll. Spot ticks too
// fast for this to be useful (it'd be a constant strobe) so spot tiles
// don't get a pulseKey — they update silently.
const tickPulse = keyframes`
  0%   { background-color: rgba(40, 114, 226, 0.20); }
  100% { background-color: transparent; }
`;

const fmtPrice = (v: number | null) => {
  if (v === null || !Number.isFinite(v)) return '—';
  return new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(v);
};

const fmtPrice4 = (v: number | null) => {
  if (v === null || !Number.isFinite(v)) return '—';
  return v.toLocaleString('en-GB', { minimumFractionDigits: 4, maximumFractionDigits: 4 });
};

// Magnitude-aware gamma formatter — mirrors the existing risk-band widget.
const fmtGamma = (v: number | null) => {
  if (v === null || !Number.isFinite(v)) return '—';
  const a = Math.abs(v);
  const dp = a >= 1000 ? 0 : a >= 1 ? 3 : a >= 0.01 ? 4 : 6;
  return v.toLocaleString('en-GB', { minimumFractionDigits: dp, maximumFractionDigits: dp });
};

const fmtPercent = (v: number | null) => {
  if (v === null || !Number.isFinite(v)) return '—';
  return `${v.toFixed(2)}%`;
};

const fmtSignedPercent = (v: number | null) => {
  if (v === null || !Number.isFinite(v)) return '—';
  const sign = v >= 0 ? '+' : '';
  return `${sign}${v.toFixed(2)}%`;
};

// UTC midnight of the current moment — the anchor for today's H/L.
const utcMidnightTs = () => {
  const now = new Date();
  return Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
};

// Floor a Date to the nearest minute — cm_time_series requires begin/end
// aligned to the frequency boundary, otherwise the server returns an empty
// result silently.
const floorToMinute = (d: Date) => {
  const t = d.getTime();
  return new Date(t - (t % 60000));
};

// One vol query — label + tenor sent to cm_time_series. Spot is handled
// separately because the cm_time_series endpoint rejects the SPOT label
// (server-confirmed: returns -32602 Invalid params, which invalidates the
// whole request and wipes ALL tiles). Spot uses the `candlestick` method.
interface TsQuery {
  label: string;
  tenor: string;
  format: 'percent' | 'signed-percent';
}

const TS_QUERIES: TsQuery[] = [
  { label: 'ATMF', tenor: '1W', format: 'percent' },
  { label: 'ATMF', tenor: '1M', format: 'percent' },
  { label: '25RR', tenor: '1W', format: 'signed-percent' },
  { label: '25RR', tenor: '1M', format: 'signed-percent' },
];

interface TsSeriesPoint {
  current: number | null;
  high: number | null;
  low: number | null;
}

const initialPoint: TsSeriesPoint = { current: null, high: null, low: null };

// Bounded flood guard (IM-1906): a market-data poller stops re-firing only
// after this many CONSECUTIVE error responses (reset on any non-error). A
// transient -32603 (redeploy/settlement blip) recovers on the next tick; a
// genuinely unresolvable venue still stops after N polls, so it can't flood.
const MAX_CONSECUTIVE_POLL_FAILURES = 3;

// Smooth value-colour interpolation across today's [low, high] range:
//   value = mid (midpoint of low and high) → neutral (text.primary)
//   value = high                            → pure success.main (green)
//   value = low                             → pure error.main   (red)
//   linear blend in between
//
// Uses CSS color-mix so the browser does the channel-by-channel interp
// against whatever the theme tokens resolve to. Returns null when there's
// no meaningful range yet (caller falls back to the neutral text colour).
const valueColourGradient = (
  value: number | null,
  low: number | null,
  high: number | null,
  neutral: string,
  green: string,
  red: string,
): string => {
  if (value === null || low === null || high === null || high === low) return neutral;
  const mid = (low + high) / 2;
  if (value === mid) return neutral;
  if (value > mid) {
    const t = Math.min(1, Math.max(0, (value - mid) / (high - mid)));
    return `color-mix(in srgb, ${neutral}, ${green} ${t * 100}%)`;
  }
  const t = Math.min(1, Math.max(0, (mid - value) / (mid - low)));
  return `color-mix(in srgb, ${neutral}, ${red} ${t * 100}%)`;
};

// One tile in the TODAY row. `value` is the live reading; `low` / `high`
// scope today's range and drive the tint. Right side stacks H / L numbers
// in the same compact mono style as the reference design.
interface TodayTileProps {
  label: string;
  tenor?: string; // optional sub-label rendered centred below `label`
  value: number | null;
  low?: number | null;
  high?: number | null;
  format: (v: number | null) => string;
  // Override the value colour. When unset, derived from H/L tint (today row).
  // Portfolio row passes an explicit colour (sign-coloured for PV/Theta,
  // greek-coloured for Delta/Vega, neutral for Gamma).
  valueColourOverride?: string;
  // Render the H/L mini-table slot. When false, the slot is omitted entirely
  // (portfolio row has no H/L). Tile box width still respects minWidth so
  // tiles in both rows line up vertically.
  showRange?: boolean;
  // When provided AND it changes, the tile briefly flashes a cyan tint
  // (300ms). Use for slow-cadence updates (vol polls every 30s); LEAVE
  // OFF for spot ticks (per-second strobe is distracting).
  pulseKey?: number | string;
}

const TodayTile: FC<TodayTileProps> = ({
  label,
  tenor,
  value,
  low = null,
  high = null,
  format,
  valueColourOverride,
  showRange = true,
  pulseKey,
}) => {
  const theme = useTheme();

  // Box stays the same neutral chrome on all tiles. Only the VALUE text
  // colour shifts smoothly red → neutral → green based on the spot's
  // position within today's [low, high] range. See valueColourGradient
  // header for the interpolation rule.
  const valueColour = useMemo(() => {
    if (valueColourOverride) return valueColourOverride;
    return valueColourGradient(
      value,
      low,
      high,
      theme.palette.text.primary,
      theme.palette.success.main,
      theme.palette.error.main,
    );
  }, [value, low, high, valueColourOverride, theme]);

  return (
    <Box
      key={pulseKey}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1.25,
        py: 0.75,
        borderRadius: '4px',
        border: '1px solid #416077',
        backgroundColor: 'rgba(40, 114, 226, 0.06)',
        minWidth: 200,
        animation: pulseKey !== undefined ? `${tickPulse} 1200ms ease-out` : 'none',
      }}
    >
      {/* Label slot: fixed width so every tile has the same label column,
          centred so the main label and optional tenor align in a tidy
          stack (ATMF / 1W, 25RR / 1M, PV, Spot, etc). */}
      <Stack spacing={0} alignItems="center" sx={{ minWidth: 36, flexShrink: 0 }}>
        <Typography
          sx={{
            fontSize: 10,
            letterSpacing: '0.10em',
            textTransform: 'uppercase',
            color: 'text.secondary',
            fontWeight: 500,
            lineHeight: 1.2,
          }}
        >
          {label}
        </Typography>
        {tenor && (
          <Typography
            sx={{
              fontSize: 9,
              letterSpacing: '0.08em',
              color: 'text.disabled',
              fontWeight: 500,
              lineHeight: 1.2,
            }}
          >
            {tenor}
          </Typography>
        )}
      </Stack>
      <Typography
        sx={{
          fontSize: 14,
          fontWeight: 700,
          fontFamily: 'Inter',
          fontVariantNumeric: 'tabular-nums',
          color: valueColour,
          flexGrow: 1,
          textAlign: 'center',
        }}
      >
        {format(value)}
      </Typography>
      {/* H/L mini-table: H/L labels in a narrow centred slot, values to
          the right of them flush right. Cluster H/L close to their numbers
          rather than space-between'ing them across the whole column.
          On portfolio tiles (showRange=false) the slot is omitted; tile
          minWidth keeps both rows aligned. */}
      {showRange && (
        <Stack
          spacing={0}
          sx={{
            fontFamily: 'Inter',
            fontVariantNumeric: 'tabular-nums',
            color: 'text.secondary',
            minWidth: 64,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <Typography
              component="span"
              sx={{ fontSize: 9.5, width: 10, textAlign: 'center', color: 'text.secondary' }}
            >
              H
            </Typography>
            <Typography
              component="span"
              sx={{ fontSize: 9.5, flex: 1, textAlign: 'right', color: 'text.secondary' }}
            >
              {format(high)}
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <Typography
              component="span"
              sx={{ fontSize: 9.5, width: 10, textAlign: 'center', color: 'text.secondary' }}
            >
              L
            </Typography>
            <Typography
              component="span"
              sx={{ fontSize: 9.5, flex: 1, textAlign: 'right', color: 'text.secondary' }}
            >
              {format(low)}
            </Typography>
          </Stack>
        </Stack>
      )}
    </Box>
  );
};

const LockedTokenHeader: FC = () => {
  const { lockedToken, instruments, results } = useContext(PricingContext);
  const { sendMessage, subscribeToChannel, unsubscribeFromChannel, isConnected } =
    useContext(WebSocketContext);
  const { tokens: availableTokens } = useAvailableTokens();
  const theme = useTheme();

  // Venue-aware market data (IM-1906). The Spot (candlestick), ATMF/25RR
  // (cm_time_series) and live-ticker requests below were hardcoded to
  // SourceTypeEnum.DERIBIT with the base_quote as the underlying. That errors
  // -32603 (deribit.underlying_enum) on every 30s poll for a non-deribit token
  // (e.g. Binance-only TRUMP_USDT). Resolve the locked token's real source and
  // venue-native symbol from the registry instead:
  //   deribit -> source DERIBIT, underlying = base_quote      ("SOL_USDC", "BTC")
  //   binance -> source BINANCE, underlying = exchange_symbol ("TRUMPUSDT")
  // WS-verified: candlestick(BINANCE,"BTCUSDT") returns bars, while
  // candlestick(DERIBIT,"SOL_USDC-PERPETUAL") errors — so deribit MUST keep the
  // bare base_quote, NOT the "-PERPETUAL" exchange_symbol.
  const lockedTokenInfo = useMemo(
    () => availableTokens.find((t) => t.baseQuote === lockedToken),
    [availableTokens, lockedToken],
  );
  // The DERIBIT fallback is only the pre-registry-load transient (the token
  // isn't in availableTokens yet). It self-heals: availableTokens is cached
  // module-scope, and marketSource is in the poll effects' deps, so the polls
  // re-fire with the correct source the moment the registry lands.
  const marketSource = lockedTokenInfo
    ? normalizeExchangeToSource(lockedTokenInfo.source)
    : SourceTypeEnum.DERIBIT;
  // Coerce to string: the poll effects below early-return when lockedToken is
  // falsy, so '' is never actually sent — it only satisfies the request-class
  // `underlying: string` signature (lockedToken is Token | null at this point).
  const marketUnderlying: string =
    (marketSource === SourceTypeEnum.DERIBIT
      ? lockedToken
      : (lockedTokenInfo?.exchangeSymbol ?? lockedToken)) ?? '';

  // IM-1907: ATMF/25RR tiles. Deribit-listed tokens have a stored intraday
  // vol timeseries (cm_time_series) → value + High/Low, kept unchanged
  // below. Auto-generated / non-deribit tokens have NO stored vol history
  // (cm_time_series returns nothing), so their CURRENT ATMF/25RR come from
  // the live vol-surface snapshot instead — the same feed the market-data
  // "Implied Vol Surface" panel uses. There's no intraday history for these,
  // so their High/Low render "—".
  //
  // The surface `token` is the base_quote (e.g. "MORPHO_USDT"), exactly what
  // the market-data page passes to subscribe.latest_vol_surface
  // (resolveBaseQuoteSymbolByExchange) — NOT the exchange_symbol used for
  // candlestick/cm_time_series. Gated to non-deribit so deribit tokens don't
  // open a redundant surface channel.
  const isDeribitSource = marketSource === SourceTypeEnum.DERIBIT;
  const surfaceToken = lockedTokenInfo?.baseQuote ?? '';
  const { tenors: surfaceTenors, lastUpdated: surfaceUpdatedAt } = useLiveVolSurface(
    surfaceToken,
    !isDeribitSource && surfaceToken !== '',
  );

  // The WS context returns fresh function references on every parent render,
  // which would thrash useEffect if listed in the deps array. Capture in
  // refs so we always call the latest version without re-firing the effect.
  const wsRef = useRef({ sendMessage, subscribeToChannel, unsubscribeFromChannel });
  useEffect(() => {
    wsRef.current = { sendMessage, subscribeToChannel, unsubscribeFromChannel };
  });

  // Vol series — current/high/low for each TS_QUERIES entry since UTC midnight.
  // Index matches TS_QUERIES order. Refreshed every 30s by polling
  // cm_time_series.
  const [series, setSeries] = useState<TsSeriesPoint[]>(() => TS_QUERIES.map(() => initialPoint));
  // Spot history — populated by the 30s candlestick poll. Stored as raw
  // arrays so we can merge with the live tick into a derived spotPoint.
  const [candleHistory, setCandleHistory] = useState<{
    highs: number[];
    lows: number[];
    closes: number[];
  }>({ highs: [], lows: [], closes: [] });
  // Live spot — pushed by the subscribe.ticker channel (same feed the
  // market data page uses). Drives the displayed "current" value.
  // No pulse — ticking once a second would be a strobe; the H/L numbers
  // and value text changing are signal enough.
  const [liveSpot, setLiveSpot] = useState<number | null>(null);
  // Vols-fresh timestamp — bumped each time the 30s cm_time_series poll
  // returns. Drives the soft pulse on vol tiles so a trader sees a vol
  // refresh land.
  const [volUpdatedAt, setVolUpdatedAt] = useState<number | null>(null);

  // Anchor today's high/low to UTC midnight — when the day rolls over, we
  // reset the range so the tiles reflect the new session, not yesterday's.
  const midnightRef = useRef<number>(utcMidnightTs());

  // Reset state scoped to the locked token — switching tabs (e.g. BTC -> ETH)
  // should clear the previous token's series so tiles don't read stale.
  useEffect(() => {
    setSeries(TS_QUERIES.map(() => initialPoint));
    setCandleHistory({ highs: [], lows: [], closes: [] });
    setLiveSpot(null);
    setVolUpdatedAt(null);
    midnightRef.current = utcMidnightTs();
  }, [lockedToken]);

  // Live spot — subscribe.ticker channel via the existing TokenData
  // request shape. Updates `liveSpot` on every tick and bumps
  // `spotTickAt` so the tile pulses live (independent of the 30s candle
  // poll). Same wire format that the market data page uses for spot.
  //
  // ROUTING GOTCHA (see memory: reference_diletta_ws_id_routing):
  // The handleUpdateMessage routing FILTERS OUT any key starting with
  // SUBSCRIPTIONS_KEY.TOKEN_DATA — that path is reserved for the market
  // data page's singular `TOKEN_DATA_${currency}` subscription. We use
  // CANDLE_SUBSCRIPTION as the prefix substring so the id (a) still
  // matches `isSubscriptionKey` for the channel-confirmation path, and
  // (b) routes through the generic update path so our handler fires.
  useEffect(() => {
    // Deribit-only: the handler below reads `params.data.deribit.index_price`,
    // so this live overlay is meaningful only for deribit tokens. Non-deribit
    // tokens get their spot from the candlestick poll below instead (a truly
    // multi-venue live ticker needs a handler change — follow-up). (IM-1906)
    if (!lockedToken || !isConnected || marketSource !== SourceTypeEnum.DERIBIT) return;
    const ws = wsRef.current;
    if (!ws.sendMessage || !ws.subscribeToChannel) return;

    const subId = `${SUBSCRIPTIONS_KEY.CANDLE_SUBSCRIPTION}_locked_live_${lockedToken}`;
    const handler = (raw: unknown) => {
      // Wire shape: { params: { data: { deribit: { index_price } } } }.
      // Walk it defensively; any missing hop yields undefined and bails.
      const rec = (v: unknown): Record<string, unknown> | undefined =>
        v && typeof v === 'object' ? (v as Record<string, unknown>) : undefined;
      const idx = rec(rec(rec(rec(raw)?.params)?.data)?.deribit)?.index_price;
      if (typeof idx !== 'number' || !Number.isFinite(idx)) return;
      setLiveSpot(idx);
    };
    ws.subscribeToChannel(subId, handler);
    ws.sendMessage(
      JSON.stringify(
        new TokenDataRequest(
          subId,
          lockedToken as unknown as CurrencyTypeEnum,
          DataLabelEnum.PERPETUAL,
        ),
      ),
    );
    return () => {
      const unsub = wsRef.current.unsubscribeFromChannel;
      if (unsub) {
        try {
          unsub(subId);
        } catch {
          /* ignore */
        }
      }
    };
  }, [lockedToken, isConnected, marketSource]);

  // Spot fetcher — polls candlestick every 30s for the locked token's
  // perpetual. OHLC bars: extract last close + min low + max high since
  // UTC midnight. Runs in parallel with the vols fetcher.
  useEffect(() => {
    if (!lockedToken || !isConnected) return;
    let cancelled = false;
    let interval: ReturnType<typeof setInterval> | undefined;
    let consecutiveFailures = 0;
    const stopPolling = () => {
      if (interval !== undefined) {
        clearInterval(interval);
        interval = undefined;
      }
    };
    const safeUnsub = (id: string) => {
      const unsub = wsRef.current.unsubscribeFromChannel;
      if (unsub) {
        try {
          unsub(id);
        } catch {
          /* ignore */
        }
      }
    };
    const noteFailure = (id: string) => {
      safeUnsub(id);
      consecutiveFailures += 1;
      if (consecutiveFailures >= MAX_CONSECUTIVE_POLL_FAILURES) stopPolling();
    };

    const fetchSpot = () => {
      const ws = wsRef.current;
      if (!ws.sendMessage || !ws.subscribeToChannel) return;
      const begin = new Date(midnightRef.current);
      const end = floorToMinute(new Date());
      if (end.getTime() <= begin.getTime()) return;

      const reqId = `LOCKED_TOKEN_SPOT_${lockedToken}_${Date.now()}`;

      const handler = (raw: unknown) => {
        if (cancelled) return;
        const resp = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : undefined;
        if (resp?.error) {
          // Bounded stop: only give up after N consecutive errors (see
          // MAX_CONSECUTIVE_POLL_FAILURES). Unsubscribe the handler either way.
          noteFailure(reqId);
          return;
        }
        // Any non-error response means the venue resolved — clear the streak.
        consecutiveFailures = 0;
        if (!Array.isArray(resp?.result)) {
          safeUnsub(reqId);
          return;
        }
        // Each row is itself an unknown[]; leaf coercion below guards values.
        const rows = resp.result as unknown[][];
        if (rows.length === 0) {
          safeUnsub(reqId);
          return;
        }
        // Candlestick row shape: [ts, open, high, low, close, (volume)].
        const highs = rows.map((r) => Number(r[2])).filter(Number.isFinite);
        const lows = rows.map((r) => Number(r[3])).filter(Number.isFinite);
        const closes = rows.map((r) => Number(r[4])).filter(Number.isFinite);
        if (closes.length === 0) {
          safeUnsub(reqId);
          return;
        }
        setCandleHistory({ highs, lows, closes });
        safeUnsub(reqId);
      };

      ws.subscribeToChannel(reqId, handler);
      ws.sendMessage(
        JSON.stringify(
          new CandleRequest(
            reqId,
            begin.toISOString(),
            end.toISOString(),
            IntervalEnum.ONE_MINUTE,
            marketSource,
            marketUnderlying,
            'PERPETUAL',
          ),
        ),
      );
    };

    fetchSpot();
    interval = setInterval(fetchSpot, 30000);
    return () => {
      cancelled = true;
      stopPolling();
    };
  }, [lockedToken, isConnected, marketSource, marketUnderlying]);

  // Vol-series fetcher — polls cm_time_series every 30s for ATMF / 25RR
  // (1W and 1M). Each response is the minutely series since UTC midnight;
  // we extract current (last value), high (max), low (min) per query.
  //
  // cm_time_series gotchas (memory: reference_cm_time_series_interval +
  // reference_diletta_ws_id_routing):
  //   - interval must be HH:mm:ss, not minute-integer
  //   - request id must NOT contain a SUBSCRIPTIONS_KEY substring or the
  //     response gets routed to the subscription path
  //   - begin/end must align to the minute boundary
  useEffect(() => {
    if (!lockedToken || !isConnected) return;

    let cancelled = false;
    let interval: ReturnType<typeof setInterval> | undefined;
    let consecutiveFailures = 0;
    const stopPolling = () => {
      if (interval !== undefined) {
        clearInterval(interval);
        interval = undefined;
      }
    };
    const safeUnsub = (id: string) => {
      const unsub = wsRef.current.unsubscribeFromChannel;
      if (unsub) {
        try {
          unsub(id);
        } catch {
          /* ignore */
        }
      }
    };
    const noteFailure = (id: string) => {
      safeUnsub(id);
      consecutiveFailures += 1;
      if (consecutiveFailures >= MAX_CONSECUTIVE_POLL_FAILURES) stopPolling();
    };

    const fetchSeries = () => {
      const ws = wsRef.current;
      if (!ws.sendMessage || !ws.subscribeToChannel) return;
      const begin = new Date(midnightRef.current);
      const end = floorToMinute(new Date());
      // Skip if the window has collapsed to zero (e.g. component mounted
      // exactly at midnight before the first minute has elapsed).
      if (end.getTime() <= begin.getTime()) return;

      const reqId = `LOCKED_TOKEN_TS_${lockedToken}_${Date.now()}`;

      const handler = (raw: unknown) => {
        if (cancelled) return;
        const resp = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : undefined;
        if (resp?.error) {
          // Bounded stop: a non-resolving venue (e.g. a source with no vol
          // timeseries for this token) stops re-polling after N consecutive
          // errors so it can't flood; a transient blip recovers on the next
          // tick. Tiles fall back to N/A ("—"). Deribit never errors here, so
          // its cadence is unchanged. Unsubscribe either way. (IM-1906)
          noteFailure(reqId);
          return;
        }
        // Any non-error response means the venue resolved — clear the streak.
        consecutiveFailures = 0;
        if (!Array.isArray(resp?.result)) {
          safeUnsub(reqId);
          return;
        }
        // Each row is an unknown[]; the typeof-number guard below narrows leaves.
        const rows = resp.result as unknown[][];
        if (rows.length === 0) {
          safeUnsub(reqId);
          return;
        }

        const next = TS_QUERIES.map((_q, idx) => {
          // Column 0 is the timestamp; columns 1..N are values in the
          // same order as TS_QUERIES. Vol values come back as decimals,
          // multiply by 100 for percent display.
          const values: number[] = rows
            .map((r) => r[idx + 1])
            .filter((v): v is number => typeof v === 'number' && Number.isFinite(v))
            .map((v) => v * 100);
          if (values.length === 0) return initialPoint;
          return {
            current: values[values.length - 1],
            high: Math.max(...values),
            low: Math.min(...values),
          };
        });

        setSeries(next);
        setVolUpdatedAt(Date.now());
        safeUnsub(reqId);
      };

      ws.subscribeToChannel(reqId, handler);

      const req = new VolumeMultipleRequest(
        reqId,
        begin.toISOString(),
        end.toISOString(),
        IntervalEnum.ONE_MINUTE,
        marketSource,
        marketUnderlying,
        TS_QUERIES.map((q) => ({ label: q.label, tenor: q.tenor })),
        'SVI',
      );
      ws.sendMessage(JSON.stringify(req));
    };

    fetchSeries();
    interval = setInterval(fetchSeries, 30000);

    return () => {
      cancelled = true;
      stopPolling();
    };
  }, [lockedToken, isConnected, marketSource, marketUnderlying]);

  // Spot displayed value = live tick (preferred) falling back to the last
  // polled close. Today's H/L = extents over candlestick history AND any
  // live ticks observed since — so a brand-new high made between polls
  // shows up immediately on the tile's tint without waiting for the next
  // candle.
  const spotPoint: TsSeriesPoint = useMemo(() => {
    const lastClose = candleHistory.closes[candleHistory.closes.length - 1] ?? null;
    const current = liveSpot ?? lastClose;
    if (current === null && candleHistory.highs.length === 0) return initialPoint;
    const highCandidates = [...candleHistory.highs];
    const lowCandidates = [...candleHistory.lows];
    if (liveSpot !== null) {
      highCandidates.push(liveSpot);
      lowCandidates.push(liveSpot);
    }
    return {
      current,
      high: highCandidates.length ? Math.max(...highCandidates) : null,
      low: lowCandidates.length ? Math.min(...lowCandidates) : null,
    };
  }, [liveSpot, candleHistory]);

  // Portfolio totals — sum each greek across all instruments in this tab
  // that have a current calculation result. An instrument with no result
  // contributes 0 (i.e. ignored). Returns null per metric when there are
  // no contributing instruments at all, so tiles show '—' rather than '0'.
  const portfolio = useMemo(() => {
    const contribs = instruments.map((i) => results[i.id]).filter(Boolean);
    const n = contribs.length;
    if (n === 0) return { pv: null, delta: null, gamma: null, theta: null, vega: null };
    const sum = (pick: (r: (typeof contribs)[number]) => number) =>
      contribs.reduce((acc, r) => acc + (Number.isFinite(pick(r)) ? pick(r) : 0), 0);
    return {
      pv: sum((r) => r.pv),
      delta: sum((r) => r.delta),
      gamma: sum((r) => r.gamma),
      theta: sum((r) => r.theta),
      vega: sum((r) => r.vega),
    };
  }, [instruments, results]);

  // Colour mapping for the portfolio row mirrors the existing risk-band
  // widget (per-column greeks): PV and Theta are sign-coloured (positive
  // green, negative red), Delta is accent cyan, Vega is the greek purple,
  // Gamma stays neutral.
  const signColour = (v: number | null) => {
    if (v === null) return theme.palette.text.primary;
    return v >= 0 ? theme.palette.success.main : theme.palette.error.main;
  };

  // Portfolio metrics aligned 1:1 with the TODAY tiles above (5 columns).
  // Order: PV under Spot, Delta under ATMF 1W, Gamma under ATMF 1M,
  // Vega under 25RR 1W, Theta under 25RR 1M.
  const portfolioTiles = [
    { label: 'PV', value: portfolio.pv, format: fmtPrice, colour: signColour(portfolio.pv) },
    {
      label: 'Delta',
      value: portfolio.delta,
      format: fmtPrice4,
      colour: theme.palette.primary.light,
    },
    {
      label: 'Gamma',
      value: portfolio.gamma,
      format: fmtGamma,
      colour: theme.palette.text.primary,
    },
    { label: 'Vega', value: portfolio.vega, format: fmtPrice, colour: '#A78BFA' },
    {
      label: 'Theta',
      value: portfolio.theta,
      format: fmtPrice,
      colour: signColour(portfolio.theta),
    },
  ];

  if (!lockedToken) return null;

  // Spot uses per-token decimals matching the market-data page (IM-1908):
  // MORPHO → 4dp, BTC → 2dp, etc. — not the flat 0dp of fmtPrice.
  const fmtSpotPrice = (v: number | null) =>
    v === null || !Number.isFinite(v) ? '—' : formatTokenPrice(v, lockedToken);

  return (
    <Box sx={{ mb: 1.5 }}>
      {/* Two-row grid so TODAY tiles and PORTFOLIO tiles align column-for-
          column. First column matches BAND_LABEL_W (120px) so the row
          labels TODAY / PORTFOLIO sit directly above the INSTRUMENT
          column in the band-row grid below. px:1.5 matches BandRow's
          internal horizontal padding so the label is visually flush. */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `${BAND_LABEL_W}px repeat(5, 200px)`,
          columnGap: 1.25,
          rowGap: 1,
          alignItems: 'center',
          px: 1.5,
        }}
      >
        {/* Row 1 — TODAY */}
        <Typography
          sx={{
            fontSize: 10,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: 'text.disabled',
            fontWeight: 500,
          }}
        >
          Today
        </Typography>
        <TodayTile
          label="Spot"
          value={spotPoint.current}
          low={spotPoint.low}
          high={spotPoint.high}
          format={fmtSpotPrice}
        />
        {TS_QUERIES.map((q, idx) => {
          const fmt = q.format === 'signed-percent' ? fmtSignedPercent : fmtPercent;
          // Source follows the token (IM-1907): deribit keeps the
          // cm_time_series series (value + live H/L); non-deribit reads the
          // current value off the live surface with no H/L (no vol history).
          const point: TsSeriesPoint = isDeribitSource
            ? series[idx]
            : {
                current: readSurfaceVolPercent(surfaceTenors, q.tenor, q.label),
                high: null,
                low: null,
              };
          const pulseKey = isDeribitSource ? volUpdatedAt : surfaceUpdatedAt;
          return (
            <TodayTile
              key={`${q.label}-${q.tenor ?? 'none'}`}
              label={q.label}
              tenor={q.tenor}
              value={point.current}
              low={point.low}
              high={point.high}
              format={fmt}
              pulseKey={pulseKey ?? undefined}
            />
          );
        })}

        {/* Divider row — thin horizontal line spanning the whole grid
            between TODAY (market) and PORTFOLIO (greeks). Uses gridColumn
            1/-1 so it stretches across every column without disturbing
            the column-by-column alignment of the tiles. */}
        <Box
          sx={{
            gridColumn: '1 / -1',
            height: '1px',
            backgroundColor: '#416077',
            opacity: 0.5,
            my: 0.5,
          }}
        />

        {/* Row 2 — PORTFOLIO totals (sum across all instruments in this tab) */}
        <Typography
          sx={{
            fontSize: 10,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: 'text.disabled',
            fontWeight: 500,
          }}
        >
          Portfolio
        </Typography>
        {portfolioTiles.map((p) => (
          <TodayTile
            key={p.label}
            label={p.label}
            value={p.value}
            format={p.format}
            valueColourOverride={p.colour}
            showRange={false}
          />
        ))}
      </Box>
    </Box>
  );
};

export default LockedTokenHeader;
